import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Upload, X, Save, Crown, Lock, Globe, Copy, Check } from "lucide-react";

const Workspace = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    agency_name: "",
    agency_slug: "",
    agency_logo: "",
    agency_color_primary: "#FF4400",
    agency_tagline: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const isAgency = user?.plan === "agency";

  useEffect(() => {
    api
      .get("/workspace/settings")
      .then((r) => setSettings({
        agency_name: r.data.agency_name || "",
        agency_slug: r.data.agency_slug || "",
        agency_logo: r.data.agency_logo || "",
        agency_color_primary: r.data.agency_color_primary || "#FF4400",
        agency_tagline: r.data.agency_tagline || "",
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800 * 1024) {
      toast.error("Logo troppo grande (max 800KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1] || "";
      setSettings((s) => ({ ...s, agency_logo: b64 }));
    };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isAgency) return;
    if (!settings.agency_name.trim()) {
      toast.error("Il nome dell'agenzia è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/workspace/settings", settings);
      setSettings({
        agency_name: data.agency_name || "",
        agency_slug: data.agency_slug || "",
        agency_logo: data.agency_logo || "",
        agency_color_primary: data.agency_color_primary || "#FF4400",
        agency_tagline: data.agency_tagline || "",
      });
      toast.success("Workspace aggiornato!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = settings.agency_slug
    ? `${window.location.origin}/w/${settings.agency_slug}`
    : "";

  const copyUrl = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("URL copiato!");
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-4 h-4 text-[#FFB800]" />
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FFB800]">
            · Agency · White-label Workspace
          </div>
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mb-3">
          Il tuo brand,<br />non il mio.
        </h1>
        <p className="text-zinc-400 font-body mb-10 max-w-2xl">
          Configura nome, logo e colore della tua agenzia. Ogni reel condiviso ai clienti
          verrà mostrato con il tuo branding al posto di Reelio.
        </p>

        {!isAgency && (
          <div className="border border-[#FFB800]/40 bg-[#FFB800]/5 rounded-sm p-6 mb-8 flex items-start gap-4" data-testid="workspace-upgrade-banner">
            <Lock className="w-5 h-5 text-[#FFB800] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-bold text-white mb-1">Richiede piano Agency</div>
              <p className="text-sm text-zinc-400 font-body mb-4">
                Il white-label è incluso nel piano Agency (€49/mese). Al momento sei sul piano{" "}
                <span className="font-mono uppercase text-white">{user?.plan || "free"}</span>.
              </p>
              <Link
                to="/pricing"
                data-testid="workspace-upgrade-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFB800] hover:bg-[#FFC933] text-black font-display font-bold rounded-sm transition-colors"
              >
                <Crown className="w-4 h-4" />
                Upgrade ad Agency
              </Link>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-3 text-zinc-500 py-20">
            <span className="w-2 h-2 rounded-full bg-[#FF4400] pulse-dot" />
            <span className="font-mono text-xs tracking-widest uppercase">Loading workspace...</span>
          </div>
        ) : (
          <form onSubmit={submit} className={`grid md:grid-cols-3 gap-8 ${!isAgency ? "opacity-50 pointer-events-none" : ""}`}>
            <div>
              <Label>Logo agenzia</Label>
              <div className="aspect-square border-2 border-dashed border-zinc-800 bg-zinc-950 rounded-sm flex items-center justify-center relative overflow-hidden group hover:border-[#FF4400] transition-colors">
                {settings.agency_logo ? (
                  <>
                    <img
                      src={`data:image/png;base64,${settings.agency_logo}`}
                      alt="Logo"
                      className="w-full h-full object-contain p-4"
                    />
                    <button
                      type="button"
                      onClick={() => setSettings((s) => ({ ...s, agency_logo: "" }))}
                      className="absolute top-2 right-2 w-7 h-7 rounded-sm bg-black/80 border border-white/10 text-white hover:bg-[#FF4400] flex items-center justify-center"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <label className="cursor-pointer flex flex-col items-center gap-2 text-zinc-500 group-hover:text-[#FF4400] transition-colors w-full h-full justify-center">
                    <Upload className="w-6 h-6" />
                    <span className="font-mono text-[10px] uppercase tracking-widest">Upload</span>
                    <span className="text-[10px] text-zinc-700">PNG/JPG max 800KB</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onLogoChange}
                      data-testid="workspace-logo-input"
                      disabled={!isAgency}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="md:col-span-2 space-y-5">
              <Field label="Nome agenzia *">
                <input
                  type="text"
                  data-testid="workspace-name-input"
                  value={settings.agency_name}
                  onChange={(e) => setSettings({ ...settings, agency_name: e.target.value })}
                  placeholder="Es. Studio Creativo Milano"
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                />
              </Field>

              <Field label="Tagline">
                <input
                  type="text"
                  data-testid="workspace-tagline-input"
                  value={settings.agency_tagline}
                  onChange={(e) => setSettings({ ...settings, agency_tagline: e.target.value })}
                  placeholder="Es. Marketing per ristoranti. Dal 2018."
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                />
              </Field>

              <Field label="Slug (URL) *">
                <div className="flex items-center border border-zinc-800 bg-zinc-900 rounded-sm overflow-hidden focus-within:ring-1 focus-within:ring-[#FF4400] focus-within:border-[#FF4400]">
                  <span className="px-3 py-3 font-mono text-xs text-zinc-500 border-r border-zinc-800 bg-zinc-950 whitespace-nowrap">
                    {window.location.host}/w/
                  </span>
                  <input
                    type="text"
                    data-testid="workspace-slug-input"
                    value={settings.agency_slug}
                    onChange={(e) => setSettings({ ...settings, agency_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                    placeholder="studiocreativo"
                    className="flex-1 px-3 py-3 bg-transparent text-white placeholder-zinc-600 focus:outline-none font-mono text-sm"
                  />
                </div>
                <p className="text-[10px] text-zinc-600 font-mono uppercase tracking-widest mt-1.5">
                  Solo lettere, numeri, trattini. 3-40 caratteri.
                </p>
              </Field>

              <Field label="Colore agenzia">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="workspace-color-input"
                    value={settings.agency_color_primary}
                    onChange={(e) => setSettings({ ...settings, agency_color_primary: e.target.value })}
                    className="w-12 h-11 rounded-sm border border-zinc-800 bg-zinc-900 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.agency_color_primary}
                    onChange={(e) => setSettings({ ...settings, agency_color_primary: e.target.value })}
                    className="flex-1 px-3 py-2.5 bg-zinc-900 border border-zinc-800 text-white font-mono text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                  />
                </div>
              </Field>

              {shareUrl && (
                <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-[#FF4400]" />
                    <Label>URL pubblico workspace</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-black border border-zinc-800 text-[#FF4400] font-mono text-xs truncate rounded-sm">
                      {shareUrl}
                    </code>
                    <button
                      type="button"
                      onClick={copyUrl}
                      data-testid="workspace-copy-url"
                      className="px-3 py-2 border border-zinc-800 hover:border-[#FF4400] text-zinc-400 hover:text-[#FF4400] rounded-sm transition-all"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={saving || !isAgency}
                  data-testid="workspace-save"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold rounded-sm transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Salva workspace
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{children}</div>
);

const Field = ({ label, children }) => (
  <div>
    <Label>{label}</Label>
    {children}
  </div>
);

export default Workspace;
